import type { BlockType } from '@unifyapps/defs/types/block';
import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { camelCase } from 'lodash';
import { getReservedBlockDisplayName } from '../../../../utils/block';
import { fixEntityName } from '../../../../utils/entityValidation/fixEntityName';
import type { ValidateEntityTypes } from '../../../../utils/entityValidation/validateEntityName';
import { getActiveInterfacePageFromStore } from '../../runner/state';
import type { InterfaceStoreStateGetterAndSetter } from '../types';

/**
 * Ensures a block's displayName is unique and in camelCase using interfacePage context.
 * If the block has a displayName, it will be converted to camelCase
 * and made unique across all entity types.
 * Merges page-level and app-level entities to ensure uniqueness across the entire app.
 */
export function ensureUniqueBlockDisplayNameWithPage({
  block,
  interfacePage,
  dataSources = {},
  appPageVariables = {},
  appPageFunctions = {},
  appBlocks = {},
  appFolders = {},
  appWalkthroughSteps = {},
}: {
  block: BlockType;
  interfacePage: InterfacePageEntity;
  dataSources?: ValidateEntityTypes['dataSources'];
  appPageVariables?: ValidateEntityTypes['pageVariables'];
  appPageFunctions?: ValidateEntityTypes['pageFunctions'];
  appBlocks?: ValidateEntityTypes['blocks'];
  appFolders?: ValidateEntityTypes['folders'];
  appWalkthroughSteps?: ValidateEntityTypes['walkthroughSteps'];
}): BlockType {
  // Reserved blocks always use their fixed displayName
  const reservedDisplayName = getReservedBlockDisplayName(block.id);
  if (reservedDisplayName) {
    if (block.displayName !== reservedDisplayName) {
      return {
        ...block,
        displayName: reservedDisplayName,
      };
    }
    return block;
  }

  // If block doesn't have a displayName, return as is
  if (!block.displayName) {
    return block;
  }

  // Get page-level entities
  const pageVariables = interfacePage.properties.pageVariables ?? {};
  const pageFunctions = interfacePage.properties.pageFunctions ?? {};
  const blocks = interfacePage.properties.blocks ?? {};
  const folders = interfacePage.properties.folders ?? {};
  const walkthroughSteps = interfacePage.properties.walkthrough?.steps ?? {};

  // Merge page-level and app-level entities for uniqueness checking across entire app
  const allPageVariables = { ...appPageVariables, ...pageVariables };
  const allPageFunctions = { ...appPageFunctions, ...pageFunctions };
  const allBlocks = { ...appBlocks, ...blocks };
  const allFolders = { ...appFolders, ...folders };
  const allWalkthroughSteps = { ...appWalkthroughSteps, ...walkthroughSteps };

  // Get unique displayName
  const uniqueDisplayName = fixEntityName({
    originalName: camelCase(block.displayName),
    entityType: 'block',
    entityId: block.id,
    dataSources,
    pageVariables: allPageVariables,
    pageFunctions: allPageFunctions,
    blocks: allBlocks,
    folders: allFolders,
    walkthroughSteps: allWalkthroughSteps,
  });

  // If displayName changed, return updated block
  if (uniqueDisplayName !== block.displayName) {
    return {
      ...block,
      displayName: uniqueDisplayName,
    };
  }

  return block;
}

/**
 * Ensures a block's displayName is unique and in camelCase.
 * If the block has a displayName, it will be converted to camelCase
 * and made unique across all entity types.
 */
export function ensureUniqueBlockDisplayName({
  block,
  storeArgs,
  pageId,
}: {
  block: BlockType;
  storeArgs: InterfaceStoreStateGetterAndSetter;
  pageId: string;
}): BlockType {
  const { get } = storeArgs;
  const state = get();
  const interfacePage = state.interfacePages[pageId];

  if (!interfacePage) {
    return block;
  }

  // Get all entity context for uniqueness checking
  const dataSources = storeArgs.getDataSourceRecords();

  // Get app-level entities for uniqueness checking across entire app
  const appInterfaceStoreState = storeArgs.getAppInterfaceStoreState?.();
  const appInterfacePage = appInterfaceStoreState
    ? getActiveInterfacePageFromStore(appInterfaceStoreState)
    : undefined;

  const appPageVariables = appInterfacePage?.properties.pageVariables ?? {};
  const appPageFunctions = appInterfacePage?.properties.pageFunctions ?? {};
  const appBlocks = appInterfacePage?.properties.blocks ?? {};
  const appFolders = appInterfacePage?.properties.folders ?? {};
  const appWalkthroughSteps = appInterfacePage?.properties.walkthrough?.steps ?? {};

  return ensureUniqueBlockDisplayNameWithPage({
    block,
    interfacePage,
    dataSources,
    appPageVariables,
    appPageFunctions,
    appBlocks,
    appFolders,
    appWalkthroughSteps,
  });
}

/**
 * Ensures a page variable's name is unique using interfacePage context.
 * If the variable has a name, it will be made unique across all entity types.
 * Merges page-level and app-level entities to ensure uniqueness across the entire app.
 */
export function ensureUniquePageVariableNameWithPage({
  pageVariable,
  interfacePage,
  dataSources = {},
  appPageVariables = {},
  appPageFunctions = {},
  appBlocks = {},
  appFolders = {},
  appWalkthroughSteps = {},
}: {
  pageVariable: JavaScriptVariable;
  interfacePage: InterfacePageEntity;
  dataSources?: ValidateEntityTypes['dataSources'];
  appPageVariables?: ValidateEntityTypes['pageVariables'];
  appPageFunctions?: ValidateEntityTypes['pageFunctions'];
  appBlocks?: ValidateEntityTypes['blocks'];
  appFolders?: ValidateEntityTypes['folders'];
  appWalkthroughSteps?: ValidateEntityTypes['walkthroughSteps'];
}): JavaScriptVariable {
  // If variable doesn't have a name, return as is
  if (!pageVariable.name) {
    return pageVariable;
  }

  // Get page-level entities
  const pageVariables = interfacePage.properties.pageVariables ?? {};
  const pageFunctions = interfacePage.properties.pageFunctions ?? {};
  const blocks = interfacePage.properties.blocks ?? {};
  const walkthroughSteps = interfacePage.properties.walkthrough?.steps ?? {};
  const folders = interfacePage.properties.folders ?? {};

  // Merge page-level and app-level entities for uniqueness checking across entire app
  const allPageVariables = { ...appPageVariables, ...pageVariables };
  const allPageFunctions = { ...appPageFunctions, ...pageFunctions };
  const allBlocks = { ...appBlocks, ...blocks };
  const allFolders = { ...appFolders, ...folders };
  const allWalkthroughSteps = { ...appWalkthroughSteps, ...walkthroughSteps };

  // Get unique name
  const uniqueName = fixEntityName({
    originalName: pageVariable.name,
    entityType: 'pageVariable',
    entityId: pageVariable.id,
    dataSources,
    pageVariables: allPageVariables,
    pageFunctions: allPageFunctions,
    blocks: allBlocks,
    folders: allFolders,
    walkthroughSteps: allWalkthroughSteps,
  });

  // If name changed, return updated variable
  if (uniqueName !== pageVariable.name) {
    return {
      ...pageVariable,
      name: uniqueName,
    };
  }

  return pageVariable;
}
